// libraries
import { type ReactNode, useContext } from 'react';

// stores
import { useCalendarNavigationStore } from '../store/calendarNavigationStore.store';
import { useShallow } from 'zustand/shallow';
import { useCalendarInteractionStore } from '../store/calendarInteractionStore.store';
import { useAppointmentStore } from 'components/mrp/stores/appointmentStore/appointmentStore';

// hooks
import { useSelector } from 'hooks';

// utils
import { useCreateShortcutContext } from 'components/shared/ShortcutProvider';
import { getShortCuts } from './utils/getShortCuts';
import { createAppointmentFormDataFromEventResponse } from 'components/mrp/components/Calendar/components/CalendarAppointment/CalenderAppointment.utils';

// components
import EntityQuickConnect from './components/EntityQuickConnect/EntityQuickConnect';
import EntityQuickDisconnect from './components/EntityQuickDisconnect/EntityQuickDisconnect';

// dialog
import { CalendarInteractionContext } from '../CalendarInteractionProvider/CalendarInteractionProvider.context';

const CalendarInteractionHandler = ({
  children
}: {
  children: ReactNode | ReactNode[];
}) => {
  'use memo';
  const { position, event } = useCalendarNavigationStore(
    useShallow((state) => ({
      position: state.position,
      event: state.event
    }))
  );

  const {
    sourceEvent,
    isCut,
    setSourceEvent,
    setShowEntityQuickConnect,
    setShowEntityQuickDisconnect
  } = useCalendarInteractionStore(
    useShallow((state) => ({
      sourceEvent: state.sourceEvent,
      isCut: state.isCut,
      isProcessing: state.isProcessing,
      setSourceEvent: state.control.setSourceEvent,
      setIsProcessing: state.control.setIsProcessing,
      setShowEntityQuickConnect: state.control.setShowEntityQuickConnect,
      setShowEntityQuickDisconnect: state.control.setShowEntityQuickDisconnect
    }))
  );

  const setAppointmentData = useAppointmentStore(
    (state) => state.control.setAppointmentData
  );

  const {
    pasteEvent: pasteEventUtil,
    duplicateEvent: duplicateEventUtil,
    duplicateRecurringEvent: duplicateRecurringEventUtil,
    deleteAppointment: deleteAppointmentUtil
  } = useContext(CalendarInteractionContext);

  const hasAddAppointmentAccess = useSelector(
    ({ grants }) => grants.hasAddAppointmentAccess
  );

  const pasteEvent = () => {
    if (!position) return;

    pasteEventUtil(position.calendarId, position.date);
  };

  const duplicateEvent = () => {
    // Only allow for read-only appointments
    if (!event || event.planningEvent.isEditable) return;

    duplicateEventUtil(event);
  };

  const duplicateRecurringEvent = () => {
    // Only allow for read-only appointments
    if (!event || event.planningEvent.isEditable) return;

    duplicateRecurringEventUtil(event);
  };

  const cutOrCopyEvent = (isCut: boolean) => {
    if (!event || (!event.planningEvent.isEditable && isCut)) return;

    setSourceEvent(event, isCut);
  };

  const openAppointment = () => {
    if (!event || !event.planningEvent.isEditable) return;

    const formData = createAppointmentFormDataFromEventResponse(
      event.planningEvent
    );

    if (formData) setAppointmentData(formData);
  };

  const openEntityQuickConnect = () => {
    if (!event || !event.planningEvent.isEditable) return;

    setShowEntityQuickConnect(true);
  };

  const openEntityQuickDisconnect = () => {
    if (!event || !event.planningEvent.isEditable) return;

    setSourceEvent(event, false, false);
    setShowEntityQuickDisconnect(true);
  };

  const deleteAppointment = () => {
    if (!event?.planningEvent?.isEditable) return;

    const formData = createAppointmentFormDataFromEventResponse(
      event.planningEvent
    );

    if (formData) deleteAppointmentUtil(formData);
  };

  useCreateShortcutContext(
    'calendar-interaction',
    getShortCuts({
      cutEvent: () => cutOrCopyEvent(true),
      copyEvent: () => cutOrCopyEvent(false),
      cancelInteraction: () => setSourceEvent(undefined, false),
      pasteEvent,
      duplicateEvent,
      duplicateRecurringEvent,
      openEntityQuickConnect,
      openEntityQuickDisconnect,
      openAppointment,
      deleteAppointment,
      canEditAppointment: !!event?.planningEvent?.isEditable,
      hasAddAppointmentAccess
    }),
    [
      position,
      event,
      sourceEvent,
      isCut,
      hasAddAppointmentAccess,
      deleteAppointment
    ]
  );

  return (
    <>
      <EntityQuickConnect />
      <EntityQuickDisconnect />
      {children}
    </>
  );
};

export default CalendarInteractionHandler;
